import type { ReactNode } from 'react';
import { cn } from '../cn.ts';

/**
 * `level` là CẤP THẺ (h1..h4), `size` là VAI TRÒ TRONG THIẾT KẾ. Tách hai thứ này ra là có chủ
 * đích: thứ tự heading phải liền mạch h1 → h2 → h3 vì cả screen reader và Google đọc cấu trúc
 * đó, nhưng thiết kế đôi khi cần một h3 to hoặc một h2 nhỏ.
 *
 * `size` KHÔNG dùng thang áo (sm/md/lg): nó dùng đúng tên vai của thiết kế, để người implement
 * đọc `template.html` thấy `font-size:40px` là tra được ngay ra `size="h2"` mà không phải đoán.
 * Bảng ánh xạ 28 cỡ → 16 token: `reference.md` §4.1.
 *
 * Không có breakpoint nào ở đây: thang chữ đã `clamp()` nội suy 360 → 1280 (skill luật 2).
 */
export interface HeadingProps {
  readonly children: ReactNode;
  readonly level: 1 | 2 | 3 | 4;
  readonly size?:
    | 'hero'
    | 'display'
    | 'h2-large'
    | 'h2'
    | 'h2-compact'
    | 'title-lg'
    | 'title'
    | 'title-bold'
    | 'title-sm';
  /**
   * Nền sáng dùng `default`; 4 tone tối của `Section` (`navy` `black` `gradient-ai` `gradient-cta`)
   * dùng `on-dark`.
   *
   * Vì sao phải là prop chứ không thừa hưởng từ `<section>`: màu chữ khai TRỰC TIẾP trên `<h2>`
   * luôn thắng màu thừa hưởng, nên màu trắng ở thẻ cha không bao giờ đè được màu chữ đặc ở đây.
   * Và không giải bằng cách nhồi class vào `className` — thắng/thua khi đó phụ thuộc thứ tự
   * Tailwind xếp utility trong CSS, không phải thứ tự chuỗi.
   */
  readonly tone?: 'default' | 'on-dark';
  readonly id?: string;
  readonly className?: string;
}

const SIZE = {
  hero: 'font-display text-hero font-extrabold tracking-tighter',
  display: 'font-display text-display font-extrabold tracking-tighter',
  // Bậc h2 LỚN của thiết kế (42px) — dùng ở #modules. Xem tokens.css.
  'h2-large': 'font-display text-h2-large font-bold tracking-tight',
  h2: 'font-display text-h2 font-bold tracking-tight',
  // Bậc h2 nhỏ của thiết kế (38px ở artboard) — dùng ở `#pm` và `#knowledge`. Xem `tokens.css`.
  'h2-compact': 'font-display text-h2-compact font-bold tracking-tight',
  'title-lg': 'text-title-lg font-bold',
  title: 'text-title font-semibold',
  /*
   * Cỡ 16px ở weight 700. Thiết kế dùng 16px ở CẢ HAI weight và hai chỗ khác nhau: summary của
   * `#faq` là 600 (`size` `title`), tiêu đề card `#security` là 700 (dòng 1004 của bản thiết kế).
   * Trước bậc này, `Heading` không có đường nào cho ra 700 ở cỡ đó, nên card `#security` phải viết
   * thẻ trần — đó là nợ D-4 của lô D, trả ở vé #10.
   *
   * Vì sao là một bậc `size` chứ không phải một prop `weight`: hai utility cùng thuộc tính
   * `font-weight` mà cùng sống thì thắng thua do thứ tự Tailwind xếp trong CSS, không do thứ tự ta
   * viết — `cn()` chỉ nối chuỗi. Một prop `weight` mở ra tổ hợp cỡ×weight mà thiết kế không dùng,
   * và mỗi tổ hợp là một chỗ để lỗi đó xảy ra. Bảng này giữ bất biến: mỗi bậc đúng MỘT utility
   * cho mỗi thuộc tính.
   *
   * ⚠️ Bậc này là bậc ĐẦU TIÊN mà tên `size` KHÔNG trùng tên một token cỡ chữ, và điều đó là tất
   * yếu chứ không phải đặt tên kém: token cỡ chữ chỉ mang `font-size`, nên "16px ở weight 700"
   * không thể có token cùng tên. Phép kiểm trong `Heading.test.tsx` vì thế neo theo BẢNG ÁNH XẠ
   * bậc → token, không neo theo phép trùng tên.
   */
  'title-bold': 'text-title font-bold',
  'title-sm': 'text-title-sm font-bold',
} as const;

const TONE = {
  default: 'text-ink-900',
  'on-dark': 'text-white',
} as const;

export function Heading({
  children,
  level,
  size = 'h2',
  tone = 'default',
  id,
  className,
}: HeadingProps) {
  const Tag = `h${String(level)}` as 'h1' | 'h2' | 'h3' | 'h4';
  /*
   * `text-balance` CHỈ cho hai vai mà thiết kế thật sự cân dòng: h1 của hero (`template.html:613`)
   * và h2 của `#cta` (`:1044`). Grep cả file: `text-wrap:balance` xuất hiện đúng hai lần.
   *
   * Bản đầu áp cho MỌI heading. Hậu quả đo được ở `#pm`: h2 tự cân thành dòng ngắn hơn, hộp chữ
   * rộng 420px thay vì 536px của thiết kế, và mọi thứ dưới nó tụt xuống. Một class "cho đẹp" áp
   * vô điều kiện là một quyết định thiết kế lặng lẽ.
   */
  const balance = size === 'hero' || size === 'display';
  return (
    <Tag
      {...(id === undefined ? {} : { id })}
      className={cn(balance && 'text-balance', TONE[tone], SIZE[size], className)}
    >
      {children}
    </Tag>
  );
}
